import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logoMdigi from '../assets/partners/mdigi.png';
import logoMdsi from '../assets/partners/mdsi.png';
import logoZerktouni from '../assets/partners/zerktouni.png';
import { HeroCarousel } from '../components/HeroCarousel';
import { ImgPlaceholder } from '../components/ImgPlaceholder';
import { MapSection } from '../components/MapSection';
import { StateMessage } from '../components/StateMessage';
import {
  useEvenements,
  useFeaturedGallery,
  useFormations,
  useGallery,
  usePartenaires,
  useSiteContent,
  useTemoignages,
} from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import { trackEvent, trackEvenementVedetteClick } from '../lib/track';
import type { FormationDomaine, Tone } from '../types';

/**
 * Fallback stat tiles when `siteContent.stats` is empty or has fewer than the
 * 4 slots that the home design provides. Kept in sync with the original
 * hardcoded values so a fresh deploy never shows blank cards.
 */
const FALLBACK_STATS: { value: string; label: string }[] = [
  { value: '250+', label: 'praticiens formés' },
  { value: '98%', label: 'recommanderaient MTC' },
  { value: '47', label: 'sessions par an' },
  { value: '112h', label: 'moyenne par participant' },
];

/**
 * Split a stat value like "1 200+", "98%", "112h" into the main number and
 * a trailing suffix so the existing `.md-stat-num span` styling (smaller,
 * italic, lower opacity) still applies to the unit. Values without a
 * non-numeric tail (e.g. "47") just return main + empty suffix.
 */
function splitStatValue(value: string): { main: string; suffix: string } {
  const trimmed = value.trim();
  const m = /^(.*?)([^\d\s,.]+)$/.exec(trimmed);
  if (m && m[1] !== undefined && m[1].length > 0) {
    return { main: m[1].trim(), suffix: m[2] ?? '' };
  }
  return { main: trimmed, suffix: '' };
}

function StatNumber({ value }: { value: string }) {
  const { main, suffix } = splitStatValue(value);
  return (
    <div className="md-stat-num">
      {main}
      {suffix && <span>{suffix}</span>}
    </div>
  );
}

const PARTNERS = [
  { name: 'Centre Dentaire Zerktouni', logo: logoZerktouni, alt: 'Centre Dentaire Zerktouni' },
  { name: 'MDSI', logo: logoMdsi, alt: 'Moroccan Dental Speakers Institute' },
  { name: 'Mdigi', logo: logoMdigi, alt: 'Mdigi — You dream it, we make it' },
] as const;

const TONE_MAP: Record<FormationDomaine, Tone> = {
  Implantologie: 'vert',
  Orthodontie: 'rouge',
  Endodontie: 'or',
  Parodontologie: 'vert',
  'Esthétique dentaire': 'rouge',
  'Chirurgie orale': 'or',
  Pédodontie: 'vert',
};

const MARQUEE_ITEMS = [
  'Implantologie',
  'Orthodontie',
  'Endodontie',
  'Parodontologie',
  'Esthétique',
  'Chirurgie',
  'Pédodontie',
  'Hands-on',
  'Live surgery',
  'Certifiant',
];

export function Home() {
  const navigate = useNavigate();
  const formationsQ = useFormations();
  const testimonialsQ = useTemoignages();
  const siteQ = useSiteContent();
  const partenairesQ = usePartenaires();
  // Home carousel uses the admin-curated "vedette" subset; falls back to the
  // full gallery on first-run (before any photo has been flagged) so the
  // section never goes empty on a fresh deploy.
  const evenementsQ = useEvenements();
  const featuredGalleryQ = useFeaturedGallery();
  const allGalleryQ = useGallery();
  const galleryQ = {
    data:
      featuredGalleryQ.data && featuredGalleryQ.data.length > 0
        ? featuredGalleryQ.data
        : allGalleryQ.data,
    isLoading: featuredGalleryQ.isLoading || allGalleryQ.isLoading,
  };
  const [time, setTime] = useState(0);

  useSEO({
    title: 'MTC — Centre de formation continue dentaire au Maroc',
    description:
      'Formations hands-on en implantologie, orthodontie, endodontie, parodontologie. Casablanca, Rabat, Marrakech. Programmes 2026 — sessions courtes, cohortes restreintes.',
  });

  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const loop = (t: number) => {
      setTime((t - start) / 1000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ── Featured hosted-MTC event (vedette événement) ─────────────────────
  // First upcoming evenement marked featured + registrationMode='hosted-mtc'.
  // Block is rendered conditionally — when nothing matches, nothing shows.
  const featuredEvent = useMemo(() => {
    const list = evenementsQ.data ?? [];
    const now = Date.now();
    return list.find(
      (e) =>
        e.featured &&
        e.registrationMode === 'hosted-mtc' &&
        e.hostedRegistration &&
        new Date(e.endDate).getTime() >= now,
    );
  }, [evenementsQ.data]);

  if (formationsQ.isLoading || testimonialsQ.isLoading) return <StateMessage variant="loading" />;
  if (formationsQ.error) return <StateMessage variant="error" message={String(formationsQ.error)} />;

  const formations = formationsQ.data ?? [];
  const testimonials = testimonialsQ.data ?? [];
  const gallery = galleryQ.data ?? [];
  // Pick the admin-flagged "vedette" formation first. If none is marked,
  // fall back to the next session by date (so the home never goes empty),
  // and finally to the first formation in the list.
  const featured =
    formations.find((f) => f.featured) ??
    [...formations].sort((a, b) => (a.dateStart ?? '').localeCompare(b.dateStart ?? ''))[0] ??
    formations[0];
  if (!featured) return <StateMessage variant="empty" />;

  // Pull up to 4 stats from siteContent — fall back to the position's
  // original hardcoded value/label when the admin hasn't filled that slot.
  const adminStats = siteQ.data?.stats ?? [];
  const statAt = (i: number) => adminStats[i] ?? FALLBACK_STATS[i]!;

  return (
    <div className="home-modern">
      <section className="md-hero">
        <div
          className="md-blob md-blob-1"
          style={{
            transform: `translate(${Math.sin(time * 0.4) * 40}px, ${Math.cos(time * 0.3) * 30}px)`,
          }}
        />
        <div
          className="md-blob md-blob-2"
          style={{
            transform: `translate(${Math.cos(time * 0.35) * 50}px, ${Math.sin(time * 0.5) * 40}px)`,
          }}
        />
        <div
          className="md-blob md-blob-3"
          style={{
            transform: `translate(${Math.sin(time * 0.25) * 30}px, ${Math.cos(time * 0.4) * 40}px)`,
          }}
        />
        <div className="md-grain" />

        <div className="md-hero-inner">
          <div className="md-hero-text">
          <h1 className="md-hero-title">
            <span className="md-line md-line-1">Former</span>
            <span className="md-line md-line-2">
              la
              <span className="md-pill md-pill-accent">prochaine</span>
            </span>
            <span className="md-line md-line-3">génération</span>
            <span className="md-line md-line-4">
              <span className="md-italic">dentaire</span>
              <span className="md-pill md-pill-primary">
                <span className="md-flag-svg">
                  <svg viewBox="0 0 30 20" width="1.2em" height="0.8em" preserveAspectRatio="none">
                    <rect width="30" height="20" fill="#C8102E" />
                    {/* Moroccan flag star: green pentagram drawn as 5 straight
                        lines crossing each other (skip-one connections between
                        5 points on a regular pentagon). Stroke-only — the inner
                        pentagon stays red, matching the official flag. */}
                    <path
                      d="M15 5 L17.94 14.045 L10.245 8.455 L19.755 8.455 L12.06 14.045 Z"
                      fill="none"
                      stroke="#0B5D3B"
                      strokeWidth="1"
                      strokeLinejoin="miter"
                      strokeMiterlimit="4"
                    />
                  </svg>
                </span>
                Maroc
              </span>
            </span>
          </h1>

          <div className="md-hero-foot">
            <div className="md-hero-lede">
              <div className="md-hero-stars">
                ★★★★★ <span>5/5 · 250+ praticiens formés</span>
              </div>
              <p>
                Centre de formation continue dédié aux chirurgiens-dentistes, jeunes diplômés et
                étudiants. Programmes hands-on, intervenants reconnus, cohortes restreintes.
              </p>
            </div>
            <div className="md-hero-cta">
              <button type="button" className="md-btn md-btn-fill" onClick={() => navigate('/formations')}>
                Voir le programme
                <span className="md-btn-arrow">→</span>
              </button>
              <button
                type="button"
                className="md-btn md-btn-outline"
                onClick={() => {
                  trackEvent('sinscrire_click');
                  navigate('/inscription');
                }}
              >
                S'inscrire
              </button>
            </div>
          </div>
          </div>{/* /md-hero-text */}

          <HeroCarousel images={gallery} limit={10} />

          <div className="md-scroll-hint">
            <span>défiler</span>
            <div className="md-scroll-line" />
          </div>
        </div>

        <div className="md-marquee">
          <div className="md-marquee-track">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((m, i) => (
              <span key={i} className="md-marquee-item">
                <span className="md-marquee-star">✦</span>
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="md-featured">
        <div className="md-featured-label">
          <span className="md-eyebrow">
            <span className="md-eyebrow-idx md-eyebrow-idx-accent">01</span>Session vedette
          </span>
          <span className="md-featured-rule" aria-hidden="true" />
          <span className="md-featured-arrow">↓</span>
        </div>
        <button
          type="button"
          className={`md-featured-card md-featured-tone-${featured.couleur}`}
          onClick={() => navigate(`/formations/${featured.id}`)}
        >
          <div className="md-featured-img">
            <ImgPlaceholder
              label="implantologie clinique"
              ratio="3/2"
              tone="vert"
              src={featured.heroImage}
              alt={featured.titre}
              fit="contain"
            />
            <div className="md-featured-tag md-featured-tag-1">
              {featured.programme.length} module{featured.programme.length > 1 ? 's' : ''}
            </div>
            <div
              className="md-featured-tag md-featured-tag-2"
              style={{
                background:
                  featured.statusTone === 'open'
                    ? 'var(--mtc-primary, #0B5D3B)'
                    : featured.statusTone === 'soldout'
                      ? undefined // keep default red accent from CSS
                      : 'rgba(15, 25, 22, 0.85)',
              }}
            >
              {featured.statusLabel}
            </div>
            <div className="md-featured-tag md-featured-tag-3">{featured.niveau}</div>
          </div>
          <div className="md-featured-body">
            <div className="md-featured-meta">
              <span className="md-tag md-tag-vert">{featured.domaine}</span>
              <span className={`md-tag md-featured-statut md-featured-statut-${featured.statusTone}`}>
                {featured.statusLabel}
              </span>
            </div>
            <h3 className="md-featured-title">{featured.titre}</h3>
            <p>{featured.accroche}</p>
            <div className="md-featured-foot">
              <div>
                <span className="md-mono">DATES</span>
                <strong>{featured.moduleDatesShort}</strong>
              </div>
              <div>
                <span className="md-mono">VILLE</span>
                <strong>{featured.ville}</strong>
              </div>
              <div>
                <span className="md-mono">PRIX</span>
                <strong>{featured.prixParModule.toLocaleString('fr-FR')} MAD HT / module</strong>
              </div>
              <div className="md-featured-go">
                <span>S'inscrire</span>
                <span className="md-featured-go-arrow">→</span>
              </div>
            </div>
          </div>
        </button>
      </section>

      {featuredEvent && (
        <section className="event-vedette-section">
          <div className="event-vedette-eyebrow">
            <span className="event-vedette-eyebrow-pill">★ À LA UNE</span>
            Événement partenaire — Inscription gérée par MTC
          </div>
          <button
            type="button"
            onClick={() => {
              trackEvenementVedetteClick(featuredEvent.slug);
              navigate(`/evenements/${featuredEvent.id}`);
            }}
            className={`event-vedette-card ${featuredEvent.image ? '' : 'no-image'}`}
          >
            {featuredEvent.image && (
              <div
                className="event-vedette-image"
                style={{ backgroundImage: `url(${featuredEvent.image})` }}
              >
                {featuredEvent.partnerName && (
                  <div className="event-vedette-partner-pill">
                    Organisé par {featuredEvent.partnerName}
                  </div>
                )}
              </div>
            )}
            <div className="event-vedette-body">
              <h3 className="event-vedette-title">{featuredEvent.title}</h3>
              <p className="event-vedette-desc">{featuredEvent.shortDescription}</p>
              <div className="event-vedette-meta">
                <span>
                  <span className="event-vedette-meta-label">Dates</span>
                  <span className="event-vedette-meta-value">
                    {new Date(featuredEvent.startDate).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </span>
                <span>
                  <span className="event-vedette-meta-label">Lieu</span>
                  <span className="event-vedette-meta-value">{featuredEvent.city}</span>
                </span>
              </div>
              <div className="event-vedette-cta">
                S'inscrire à l'événement <span>→</span>
              </div>
            </div>
          </button>
        </section>
      )}

      <section className="md-strips">
        <div className="md-strips-head">
          <span className="md-eyebrow">
            <span className="md-eyebrow-idx">02</span>Catalogue 2026
          </span>
          <h2 className="md-h2">
            Plusieurs disciplines.
            <br />
            <em>Une seule exigence.</em>
          </h2>
          <button
            type="button"
            className="md-btn md-btn-outline"
            onClick={() => navigate('/formations')}
          >
            Tout voir <span className="md-btn-arrow">→</span>
          </button>
        </div>
        <div className="md-strips-list">
          {formations.slice(0, 6).map((f, i) => {
            const tone = TONE_MAP[f.domaine];
            return (
              <button
                key={f.id}
                type="button"
                className={`md-strip md-strip-tone-${tone} md-strip-${i % 3}`}
                onClick={() => navigate(`/formations/${f.id}`)}
              >
                <div className="md-strip-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="md-strip-body">
                  <div className="md-strip-domain">
                    {f.domaine}
                    <span className={`md-strip-status md-strip-status-${f.statusTone}`}>
                      {f.statusLabel}
                    </span>
                  </div>
                  <h3>{f.titre}</h3>
                  <p>{f.accroche}</p>
                </div>
                <div className="md-strip-meta">
                  <div className="md-strip-date">{f.moduleDatesShort}</div>
                  <div className="md-strip-place">
                    {f.ville} · {f.programme.length} module{f.programme.length > 1 ? 's' : ''}
                  </div>
                  <div className="md-strip-price">
                    {f.prixParModule.toLocaleString('fr-FR')} <span>MAD HT / module</span>
                  </div>
                </div>
                <div className="md-strip-arrow-wrap">
                  <div className="md-strip-arrow">→</div>
                </div>
                <div className="md-strip-img">
                  <ImgPlaceholder
                    label={f.image}
                    ratio="4/3"
                    tone={tone === 'or' ? 'creme' : tone}
                    src={f.heroImage}
                    alt={f.titre}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="md-stats">
        <div className="md-stats-bg" />
        <div className="md-stats-inner">
          <div className="md-stats-head">
            <span className="md-eyebrow md-eyebrow-light">
              <span className="md-eyebrow-idx md-eyebrow-idx-light">03</span>Depuis 2025
            </span>
            <h2 className="md-h2 md-h2-light">
              Les chiffres
              <br />
              <em>parlent.</em>
            </h2>
          </div>
          <div className="md-stats-grid">
            <div className="md-stat">
              <StatNumber value={statAt(0).value} />
              <div className="md-stat-label">{statAt(0).label}</div>
              <div className="md-stat-bars">
                {[0.4, 0.6, 0.5, 0.7, 0.85, 1.0].map((h, i) => (
                  <div key={i} style={{ height: `${h * 100}%`, animationDelay: `${i * 0.1}s` }} />
                ))}
              </div>
            </div>
            <div className="md-stat md-stat-accent">
              <StatNumber value={statAt(1).value} />
              <div className="md-stat-label">{statAt(1).label}</div>
              <div className="md-stat-ring">
                <svg viewBox="0 0 100 100" width="60" height="60">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="8" />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="white"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray="251"
                    strokeDashoffset="5"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>
            <div className="md-stat">
              <StatNumber value={statAt(2).value} />
              <div className="md-stat-label">{statAt(2).label}</div>
              <div className="md-stat-dots">
                {Array.from({ length: 47 }).map((_, i) => (
                  <span key={i} style={{ animationDelay: `${i * 0.02}s` }} />
                ))}
              </div>
            </div>
            <div className="md-stat">
              <StatNumber value={statAt(3).value} />
              <div className="md-stat-label">{statAt(3).label}</div>
              <div className="md-stat-clock">
                <svg viewBox="0 0 60 60" width="50" height="50">
                  <circle cx="30" cy="30" r="26" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                  <line
                    x1="30"
                    y1="30"
                    x2="30"
                    y2="12"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    style={{ transformOrigin: '30px 30px', animation: 'clockHour 8s linear infinite' }}
                  />
                  <line
                    x1="30"
                    y1="30"
                    x2="44"
                    y2="30"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{ transformOrigin: '30px 30px', animation: 'clockMin 2s linear infinite' }}
                  />
                  <circle cx="30" cy="30" r="2.5" fill="white" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="md-manifesto">
        <div className="md-manifesto-bg" />
        <div className="md-manifesto-inner">
          <span className="md-eyebrow">
            <span className="md-eyebrow-idx">04</span>Notre conviction
          </span>
          <p className="md-manifesto-text">
            Une formation utile
            <br />
            ne se mesure pas en <span className="md-strike">heures de cours</span>
            <br />
            mais en <em>gestes</em> que l'on pose
            <br />
            <span className="md-mark">le lundi matin.</span>
          </p>
          <button type="button" className="md-btn md-btn-on-dark" onClick={() => navigate('/a-propos')}>
            Notre approche <span className="md-btn-arrow">→</span>
          </button>
        </div>
      </section>

      <section className="md-testi">
        <div className="md-testi-head">
          <span className="md-eyebrow">
            <span className="md-eyebrow-idx">05</span>Ce qu'ils en disent
          </span>
          <h2 className="md-h2">
            Des praticiens
            <br />
            <em>convaincus.</em>
          </h2>
        </div>
        <div className="md-testi-grid">
          {testimonials.map((t, i) => (
            <div key={i} className={`md-testi-card md-testi-card-${i % 4}`}>
              <div className="md-testi-stars">★★★★★</div>
              <p>« {t.texte} »</p>
              <div className="md-testi-attr">
                <strong>{t.auteur}</strong>
                <span>{t.role}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="md-partners" aria-labelledby="md-partners-title">
        <div className="md-partners-head">
          <span className="md-eyebrow">
            <span className="md-eyebrow-idx">06</span>Partenaires
          </span>
          <h2 id="md-partners-title" className="md-partners-title">
            Ils <em>partagent</em> notre exigence
          </h2>
        </div>
        <ul className="md-partners-list">
          {(partenairesQ.data ?? []).length > 0
            ? // Admin-managed partners → each links to its detail page.
              (partenairesQ.data ?? []).map((p) => (
                <li key={p.id} className="md-partner">
                  <button
                    type="button"
                    className="md-partner-link"
                    onClick={() => navigate(`/partenaires/${p.id}`)}
                    aria-label={`En savoir plus sur ${p.name}`}
                    title={p.name}
                  >
                    {p.logo ? (
                      <img src={p.logo} alt={p.name} loading="lazy" />
                    ) : (
                      <span className="md-partner-name">{p.name}</span>
                    )}
                  </button>
                </li>
              ))
            : // Fallback to the bundled logos until the admin adds partners.
              PARTNERS.map((p) => (
                <li key={p.name} className="md-partner">
                  <img src={p.logo} alt={p.alt} loading="lazy" />
                </li>
              ))}
        </ul>
      </section>

      <section className="md-final">
        <div className="md-final-orbs">
          <span className="md-final-orb md-final-orb-1" />
          <span className="md-final-orb md-final-orb-2" />
          <span className="md-final-orb md-final-orb-3" />
        </div>
        <h2 className="md-final-title">
          <em>Prêt</em> à transformer
          <br />
          votre pratique<span className="md-final-q">?</span>
        </h2>
        <p className="md-final-lede">
          Inscriptions ouvertes pour la session 2026. Places limitées.
        </p>
        <div className="md-final-cta">
          <button
            type="button"
            className="md-btn md-btn-fill md-btn-lg"
            onClick={() => navigate('/formations')}
          >
            Voir les formations <span className="md-btn-arrow">→</span>
          </button>
          <button
            type="button"
            className="md-btn md-btn-outline md-btn-lg"
            onClick={() => {
              trackEvent('sinscrire_click');
              navigate('/inscription');
            }}
          >
            S'inscrire maintenant
          </button>
        </div>
      </section>

      <MapSection />
    </div>
  );
}
